
import { defineComponent, PropType, toRef, computed, ref, watch } from "vue";
import { checked, getValueFromEvent } from "@/helpers/form";
import FieldError from "@/Components/Utilities/FieldError.vue";
import { uniq, without } from "lodash";
import Description from "@/Components/Utilities/Description.vue";
import { v4 as uuidv4 } from "uuid";

interface Props {
  label: string;
  value: string | number;
  modelValue: string | number | boolean | (string | number)[];
  required: boolean;
  disabled: boolean;
  multiple: boolean;
  description: string;
  errorMessage: string;
  id?: string;
}

export default defineComponent({
  props: {
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      required: false,
      default: null,
    },
    modelValue: {
      type: [Boolean, String, Number, Array] as PropType<
        string | number | boolean | (string | number)[]
      >,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    description: {
      type: String,
      required: false,
      default: "",
    },
    errorMessage: {
      type: String,
      required: false,
      default: "",
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    id: {
      type: String,
      required: false,
      default: null,
    },
  },
  emits: ["update:modelValue"],
  components: {
    FieldError,
    Description,
  },
  setup(props: Props, { emit }) {
    const modelValue = toRef(props, "modelValue");
    const id = toRef(props, "id");
    const propValue = toRef(props, "value");
    const hasFocus = ref(false);
    const multiple = toRef(props, "multiple");

    const computedValue = computed(() => {
      return null === propValue.value ? "1" : propValue.value;
    });

    const computedId = computed((): any => {
      return id.value ? id.value : uuidv4();
    });

    const onChange = (event: any) => {
      let checkedValues = modelValue.value;

      if (multiple.value) {
        if (!Array.isArray(checkedValues)) {
          checkedValues = [`${checkedValues}`];
        }

        if (propValue.value) {
          const newValue = getValueFromEvent(event);
          if (newValue) {
            checkedValues = [...checkedValues, `${newValue}`];
          } else if (!newValue) {
            if (checkedValues.indexOf(`${propValue.value}`) !== -1) {
              checkedValues = without(checkedValues, `${propValue.value}`);
            }
          }
        }

        checkedValues = uniq(checkedValues);
      } else {
        checkedValues = getValueFromEvent(event);

        if (propValue.value === null) {
          checkedValues = checked(checkedValues);
        }
      }

      emit("update:modelValue", checkedValues);
    };

    function isChecked() {
      return multiple.value
        ? Array.isArray(modelValue.value) &&
            modelValue.value.indexOf(`${propValue.value}`) !== -1
        : null === propValue.value
        ? [1, "1", "on", "true", true].indexOf(modelValue.value as any) !== -1
        : propValue.value === modelValue.value;
    }

    return {
      hasFocus,
      // refValue,
      onChange,
      // modelValue,
      getValueFromEvent,
      computedId,
      checked,
      // propValue,
      multiple,
      isChecked,
      computedValue,
    };
  },
});
